const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 40;
const LINE_HEIGHT = 14;
const ROWS_PER_PAGE = 48;

const WIN_ANSI_OVERRIDES: Record<string, number> = {
  '€': 0x80,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
};

const toWinAnsi = (text: string) =>
  Array.from(text, (char) => {
    const code = WIN_ANSI_OVERRIDES[char] ?? char.charCodeAt(0);

    return String.fromCharCode(code <= 0xff ? code : 0x3f);
  }).join('');

const escapePdfText = (text: string) => toWinAnsi(text).replace(/[\\()]/g, (match) => `\\${match}`);

export interface PdfTableColumn {
  x: number;
  align?: 'left' | 'right';
}

export interface PdfStatementDocument {
  title: string;
  subtitle: string;
  columns: PdfTableColumn[];
  header: string[];
  rows: string[][];
}

const textOperator = (text: string, x: number, y: number, font: 'F1' | 'F2', size: number) =>
  `BT /${font} ${size} Tf ${x} ${y} Td (${escapePdfText(text)}) Tj ET`;

const rightAlignedX = (text: string, column: PdfTableColumn, size: number) =>
  column.align === 'right' ? column.x - text.length * size * 0.5 : column.x;

const buildPageContent = (
  document: PdfStatementDocument,
  rows: string[][],
  pageIndex: number,
  pageCount: number,
) => {
  const operators: string[] = [];
  let y = PAGE_HEIGHT - MARGIN - 10;

  if (pageIndex === 0) {
    operators.push(textOperator(document.title, MARGIN, y, 'F2', 16));
    y -= LINE_HEIGHT * 1.5;
    operators.push(textOperator(document.subtitle, MARGIN, y, 'F1', 10));
    y -= LINE_HEIGHT * 2;
  }

  document.header.forEach((label, index) => {
    const column = document.columns[index];

    if (column) operators.push(textOperator(label, rightAlignedX(label, column, 9), y, 'F2', 9));
  });
  y -= LINE_HEIGHT;

  rows.forEach((row) => {
    row.forEach((cell, index) => {
      const column = document.columns[index];

      if (column) operators.push(textOperator(cell, rightAlignedX(cell, column, 9), y, 'F1', 9));
    });
    y -= LINE_HEIGHT;
  });

  operators.push(
    textOperator(`${pageIndex + 1} / ${pageCount}`, PAGE_WIDTH - MARGIN - 30, MARGIN, 'F1', 8),
  );

  return operators.join('\n');
};

export const buildStatementPdf = (document: PdfStatementDocument): Blob => {
  const pages: string[][][] = [];

  for (let index = 0; index < Math.max(document.rows.length, 1); index += ROWS_PER_PAGE) {
    pages.push(document.rows.slice(index, index + ROWS_PER_PAGE));
  }

  const objects: string[] = [];
  const addObject = (body: string) => objects.push(body);

  const catalogId = addObject('');
  const pagesId = addObject('');
  const regularFontId = addObject(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
  );
  const boldFontId = addObject(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
  );

  const pageIds = pages.map((rows, pageIndex) => {
    const content = buildPageContent(document, rows, pageIndex, pages.length);
    const contentId = addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);

    return addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
        `/Resources << /Font << /F1 ${regularFontId} 0 R /F2 ${boldFontId} 0 R >> >> ` +
        `/Contents ${contentId} 0 R >>`,
    );
  });

  objects[catalogId - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;
  objects[pagesId - 1] =
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;

  let output = '%PDF-1.4\n';
  const offsets: number[] = [];

  objects.forEach((body, index) => {
    offsets.push(output.length);
    output += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });

  const xrefOffset = output.length;

  output += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => {
    output += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  output += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;

  return new Blob([Uint8Array.from(output, (char) => char.charCodeAt(0))], {
    type: 'application/pdf',
  });
};
